<template>
  <v-dialog
    v-model="opened"
    width="600"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    @keydown.esc="$emit('update:opened', false)"
    persistent
    lazy
  >
    <v-card block flat>
      <v-card-title class="title pt-4 pb-0"> Send </v-card-title>

      <v-tabs centered v-model="mode">
        <v-tab class="px-2 text-none">Send to Email</v-tab>
        <v-tab class="px-2 text-none">Send to Documents</v-tab>
      </v-tabs>

      <v-card-text class="text-xs-left">
        <v-alert :value="true" color="info" outline class="mb-2 text-xs-left">
          <span
            >You can utilize variables such as {{ userVariables }},
            {{ entityVariables
            }}{{
              mode === 1 ? ', {document_name}, and {document_path}' : ''
            }}</span
          >
        </v-alert>
        <v-layout column v-if="mode === 0">
          <b>Subject</b>
          <v-text-field
            class="default-input mt-1 pb-2"
            v-model="sendSignatureRequestOptions.subject"
            solo
            hide-details
          />
          <v-checkbox
            class="pt-0 mt-0 mb-2"
            label="Save for future use"
            v-model="sendSignatureRequestOptions.saveSubjectForFutureUse"
          />

          <v-layout row align-center>
            <b>Message</b>
          </v-layout>
          <quill-editor
            v-model="sendSignatureRequestOptions.message"
            :options="editorOption"
            class="message-input mt-2 pb-2 default-input"
          />
          <v-checkbox
            class="pt-0 mt-0 mb-2"
            label="Save for future use"
            v-model="sendSignatureRequestOptions.saveMessageForFutureUse"
          />

          <b>Options</b>

          <v-checkbox
            class="mt-1"
            label="Include DO NOT FILE watermark (pages with signatures will be excluded)"
            v-model="sendSignatureRequestOptions.includeWatermark"
            solo
            hide-details
          />
        </v-layout>

        <v-layout column v-else-if="mode === 1" class="text-xs-left">
          <b>Subject</b>
          <v-text-field
            class="default-input mt-1 pb-3"
            v-model="sendToDocumentsOptions.subject"
            solo
            hide-details
          />
          <v-checkbox
            class="pt-0 mt-0 mb-2"
            label="Save for future use"
            v-model="sendToDocumentsOptions.saveSubjectForFutureUse"
          />

          <v-layout align-center>
            <b>Message</b>
          </v-layout>
          <quill-editor
            v-model="sendToDocumentsOptions.message"
            :options="editorOption"
            class="message-input mt-2 pb-2 default-input"
          />
          <v-checkbox
            class="pt-0 mt-0 mb-2"
            label="Save for future use"
            v-model="sendToDocumentsOptions.saveMessageForFutureUse"
          />

          <v-layout mt-1>
            <b>Path: /{{ signatureRequest.tax_year }}</b>
          </v-layout>

          <v-layout mt-3>
            <v-flex xs-12>
              <b>Document Name</b>
              <v-text-field
                class="default-input mt-1 pb-3"
                v-model="sendToDocumentsOptions.documentName"
                solo
                hide-details
              />
            </v-flex>
          </v-layout>
        </v-layout>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn class="primary text-none" @click="send()" :loading="sending">
          Send
        </v-btn>
        <v-btn
          class="error--text text-none"
          flat
          @click="$emit('update:opened', false)"
        >
          Cancel
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import get from 'lodash/get';
import { entityVariables, userVariables } from '@/constants/variables';
import { mapGetters } from 'vuex';

export default {
  props: {
    opened: {
      type: Boolean,
    },

    signatureRequest: {
      type: Object,
    },

    sending: {
      type: Boolean,
    },
  },

  data() {
    return {
      entityVariables,
      userVariables,
      mode: 0,

      editorOption: {
        formats: ['bold', 'italic', 'underline', 'link', 'indent', 'list'],
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ indent: '-1' }, { indent: '+1' }],
            ['link'],
            [{ list: 'ordered' }, { list: 'bullet' }],
          ],
        },
        placeholder: 'Message',
        bounds: '.message-input',
      },

      sendSignatureRequestOptions: {
        subject: '',
        message: '',
        includeWatermark: false,
        saveSubjectForFutureUse: false,
        saveMessageForFutureUse: false,
      },

      sendToDocumentsOptions: {
        subject: '',
        message: '',
        path: '',
        documentName: '',
        saveSubjectForFutureUse: false,
        saveMessageForFutureUse: false,
      },
    };
  },

  computed: {
    ...mapGetters({
      currentFirm: 'currentFirm',
      currentUser: 'user',
    }),
  },

  methods: {
    send() {
      if (this.mode === 0) {
        this.$emit('send', {
          mode: this.mode,
          ...this.sendSignatureRequestOptions,
        });
      } else if (this.mode === 1) {
        this.$emit('send', {
          mode: this.mode,
          ...this.sendToDocumentsOptions,
        });
      }
    },

    reset() {
      this.mode = 0;

      this.sendSignatureRequestOptions.saveSubjectForFutureUse = false;
      this.sendSignatureRequestOptions.saveMessageForFutureUse = false;
      this.sendSignatureRequestOptions.subject = this.signatureRequest.subject
        ? this.signatureRequest.subject
        : get(
            this.currentFirm,
            'preferences.send_signature_request_default_subject',
            'Please review and e-sign ' + this.signatureRequest.form_name,
          );

      this.sendSignatureRequestOptions.message = this.signatureRequest.message
        ? this.signatureRequest.message
        : get(
            this.currentFirm,
            'preferences.send_signature_request_default_message',
            `<p>Hi {client_preferred_name},</p>
          <p>Please click the button below to review the return and sign the e-file authorization forms. Once that's done, I will e-file the return.</p>
          <p>Thank you,</p>
          <p>${this.currentUser.full_name}</p>`,
          );

      this.sendToDocumentsOptions.saveSubjectForFutureUse = false;
      this.sendToDocumentsOptions.saveMessageForFutureUse = false;
      this.sendToDocumentsOptions.subject = get(
        this.currentFirm,
        'preferences.send_return_to_documents_default_subject',
        'Your {tax_year} tax return is ready for e-file!',
      );

      this.sendToDocumentsOptions.message = get(
        this.currentFirm,
        'preferences.send_return_to_documents_default_message',
        `<p>Hi {client_preferred_name},</p>
        <p>Your {tax_year} tax return is ready for e-file. Please click the button below to access the portal and review {document_name} under folder {document_path}. Please print the return, sign the e-file authorization forms and then upload under the same folder. Once that's done, I will e-file the return. Please let me know if you have any questions.</p>
        <p>Best regards,</p>
        <p>${this.currentUser.full_name}</p>`,
      );

      this.sendToDocumentsOptions.path = '/' + this.signatureRequest.tax_year;

      this.sendToDocumentsOptions.documentName = this.signatureRequest.form_name;
    },
  },

  watch: {
    opened: {
      async handler(value) {
        if (value) {
          this.reset();
        }
      },
    },
  },
};
</script>

<style lang="postcss" scoped>
.message-input {
  & >>> .ql-container .ql-editor {
    min-height: 80px;
    max-height: 25vh;
  }

  & >>> .ql-container .ql-editor p,
  & >>> .ql-container .ql-editor ul {
    padding-bottom: 12px;
  }
}
</style>
